/**
 * Tiny purpose-built Markdown -> HTML renderer for the Kiey legal pages.
 *
 * Scope: only the constructs we actually use in the ToS / DPA markdown
 * sources (# / ## / ### headings, **bold** / *italic*, GFM pipe tables,
 * `code` spans, unordered / ordered lists, `> blockquotes`, `---` rules,
 * inline links, bare URLs, and paragraphs separated by blank lines).
 *
 * Why hand-rolled instead of a library:
 *  - No new npm dependency required.
 *  - Output is the exact subset the legal-page CSS is styled for.
 *  - Input is trusted (committed-in-repo legal text), so no full sanitizer
 *    is required; we still escape raw HTML and never inject `<script>`.
 *
 * If counsel adds a markdown construct we don't handle, extend this file
 * rather than swapping in a dependency.
 */

function escapeHtml(s) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function renderInline(s) {
  let out = escapeHtml(s);
  // Inline code spans first so other replacements don't touch their
  // contents.
  out = out.replace(/`([^`]+)`/g, function (_m, c) {
    return '<code>' + c + '</code>';
  });
  // Bold **...**
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // Italic *...* (only when surrounded by word boundaries; bold has
  // already been collapsed)
  out = out.replace(
    /(^|[\s(])\*([^*\n]+)\*(?=$|[\s.,;:!?)])/g,
    '$1<em>$2</em>'
  );
  // Markdown links [text](url)
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, function (_m, text, url) {
    return '<a href="' + url.trim() + '">' + text + '</a>';
  });
  // Bare http(s) URLs — exclude trailing punctuation so sentences don't
  // capture it in the href.
  out = out.replace(
    /(^|[\s(])((?:https?:\/\/)[^\s)<]+?)([.,;:!?)]*)(?=$|\s|<)/g,
    function (_m, lead, url, tail) {
      return lead + '<a href="' + url + '">' + url + '</a>' + tail;
    }
  );
  return out;
}

export function renderLegalMarkdown(src) {
  // Normalize line endings; strip per-line trailing whitespace.
  const lines = src
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map(function (l) { return l.replace(/[\t ]+$/, ''); });

  // Strip leading top-level title (# ...) and the version/effective-date
  // front-matter that lives right beneath it. The page chrome renders
  // the title + version metadata itself, so we don't want a duplicate
  // H1 + duplicate version line. We DO want any prose that follows the
  // front-matter (welcome paragraphs, intros) to survive into the
  // rendered body. So we only consume the well-known metadata fields
  // (`**Version:**`, `**Effective Date:**`, `**Last Updated:**`,
  // similar bold-prefixed key/value lines) plus surrounding blanks,
  // and stop at the first line that isn't one of those.
  const FRONT_MATTER_PREFIXES = [
    '**version',
    '**effective date',
    '**effective',
    '**last updated',
    '**updated',
    '**published',
  ];
  function isFrontMatterLine(s) {
    if (!s) return false;
    const lower = s.trim().toLowerCase();
    return FRONT_MATTER_PREFIXES.some(function (p) { return lower.startsWith(p); });
  }
  while (lines.length && lines[0].trim() === '') lines.shift();
  if (lines.length && /^# /.test(lines[0])) {
    lines.shift();
    while (lines.length && (lines[0].trim() === '' || isFrontMatterLine(lines[0]))) {
      lines.shift();
    }
    // Some legal docs put a `---` separator right after the
    // front-matter (DPA, ToS). Eat it so we don't render an awkward
    // leading horizontal rule.
    if (lines.length && /^---\s*$/.test(lines[0])) lines.shift();
    while (lines.length && lines[0].trim() === '') lines.shift();
  }

  const out = [];
  let i = 0;

  function flushParagraph(buf) {
    if (!buf.length) return;
    const text = buf.join(' ').trim();
    if (text) out.push('<p>' + renderInline(text) + '</p>');
    buf.length = 0;
  }

  while (i < lines.length) {
    const line = lines[i];

    if (/^---\s*$/.test(line)) { out.push('<hr />'); i++; continue; }
    if (line.trim() === '') { i++; continue; }

    let m;
    if ((m = line.match(/^### (.*)$/))) {
      out.push('<h3>' + renderInline(m[1].trim()) + '</h3>');
      i++; continue;
    }
    if ((m = line.match(/^## (.*)$/))) {
      out.push('<h2>' + renderInline(m[1].trim()) + '</h2>');
      i++; continue;
    }
    if ((m = line.match(/^# (.*)$/))) {
      // Treat any other H1 (e.g., SCHEDULE A) as H2 since the page
      // chrome owns the page title.
      out.push('<h2>' + renderInline(m[1].trim()) + '</h2>');
      i++; continue;
    }

    // Pipe tables (GFM) — header row then a separator row of -+:|+ chars.
    if (
      /^\|.*\|\s*$/.test(line) &&
      i + 1 < lines.length &&
      /^\|\s*[-:|\s]+\|\s*$/.test(lines[i + 1])
    ) {
      const headerCells = line
        .split('|')
        .slice(1, -1)
        .map(function (c) { return c.trim(); });
      i += 2;
      const bodyRows = [];
      while (i < lines.length && /^\|.*\|\s*$/.test(lines[i])) {
        const cells = lines[i]
          .split('|')
          .slice(1, -1)
          .map(function (c) { return c.trim(); });
        bodyRows.push(cells);
        i++;
      }
      out.push('<div class="legal-table-wrap"><table class="legal-table">');
      out.push(
        '<thead><tr>' +
          headerCells
            .map(function (c) { return '<th>' + renderInline(c) + '</th>'; })
            .join('') +
          '</tr></thead>'
      );
      out.push('<tbody>');
      for (let r = 0; r < bodyRows.length; r++) {
        out.push(
          '<tr>' +
            bodyRows[r]
              .map(function (c) {
                return '<td>' + renderInline(c) + '</td>';
              })
              .join('') +
            '</tr>'
        );
      }
      out.push('</tbody>');
      out.push('</table></div>');
      continue;
    }

    if (/^>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      out.push('<blockquote>' + renderInline(buf.join(' ').trim()) + '</blockquote>');
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      out.push('<ul>');
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        const item = lines[i].replace(/^[-*]\s+/, '');
        out.push('<li>' + renderInline(item) + '</li>');
        i++;
      }
      out.push('</ul>');
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      out.push('<ol>');
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        const item = lines[i].replace(/^\d+\.\s+/, '');
        out.push('<li>' + renderInline(item) + '</li>');
        i++;
      }
      out.push('</ol>');
      continue;
    }

    const buf = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^---\s*$/.test(lines[i]) &&
      !/^#{1,3} /.test(lines[i]) &&
      !/^[-*]\s+/.test(lines[i]) &&
      !/^\d+\.\s+/.test(lines[i]) &&
      !/^>\s?/.test(lines[i]) &&
      !(
        /^\|.*\|\s*$/.test(lines[i]) &&
        i + 1 < lines.length &&
        /^\|\s*[-:|\s]+\|\s*$/.test(lines[i + 1])
      )
    ) {
      buf.push(lines[i]);
      i++;
    }
    flushParagraph(buf);
  }

  return out.join('\n');
}

export default { renderLegalMarkdown };
